import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import * as React from 'react';
import { RouteObject } from 'react-router';
import { PermissionTypes } from '../ducks/auth';
import { getFullLogisticsUrl, logisticsRoutes, LogisticsUrls } from '../logistics/logisticsRoutes';
import { lazyComponents } from './lazyComponents';

export const DRAWER_WIDTH = 240;

export type NavTypes = 'core' | PermissionTypes;

export interface INav extends Omit<RouteObject, 'children'> {
  element: React.ReactElement;
  filterComponent?: React.ReactElement;
  name?: string;
  children?: INav[];
  permissionTypes?: PermissionTypes[];
  searchComponent?: React.ReactElement;
  sidebar?: { icon: React.ReactElement; component: React.ReactElement };
}

export enum NavUrls {
  coOpDetail = '/co-op/:id',
  dealerPortalFreeShipping = '/dealerPortal/freeShipping',
  dealerPortalNewsFeed = '/dealerPortal/newsFeed',
  dealerPortalNewsPost = '/dealerPortal/newsFeed/:id',
  financeCashDiscounts = '/finance/cashDiscounts',
  financeCoOpRequests = '/finance/coOpRequests',
  financeLinks = '/finance/links',
  financeNewDealerList = '/finance/newDealer',
  financePaymentTerms = '/finance/paymentTerms',
  financePreseason = '/finance/preseason',
  financePrograms = '/finance/programs',
  itPermissions = '/permissions/users',
  itDisplays = '/it/displays',
  itLinks = '/it/links',
  logistics = '/logistics',
  marketingAdmin = '/marketing/admin',
  marketingBigCommerce = '/marketing/bigCommerce',
  marketingCoOp = '/marketing/coOp',
  marketingLinks = '/marketing/links',
  marketingNewDealer = '/marketing/newDealer',
  newDealer = '/prospects/:id/newDealer',
  newDealerOrder = '/newDealer/:newDealerRequestId/:brand',
  operationsAdmin = '/operations/admin',
  operationsDepartingSoon = '/operations/departingSoon',
  operationsUsers = '/operations/users',
  root = '/',
  salesAdmin = '/sales/admin',
  salesCalls = '/sales/calls',
  salesCoOp = '/sales/co-op',
  salesCompliance = '/sales/compliance',
  salesDashboard = '/sales',
  salesDealer = '/sales/dealers/:accountNum',
  salesDealers = '/sales/dealers',
  salesDocuments = '/sales/documents',
  salesLinks = '/sales/links',
  salesLogistics = '/sales/logistics',
  salesLogisticsShipment = '/sales/logistics/shipment/:id',
  salesNewDealer = '/sales/newDealer',
  salesOrder = '/sales/order/:salesId',
  salesProspect = '/sales/prospects/:id',
  salesProspects = '/sales/prospects',
  salesRegionSettings = '/sales/regionsettings',
  salesReports = '/sales/reports',
  targetedCatalog = '/targetedCatalog'
}

export const getUrl = {
  carrier: (carrierId: string): string =>
    `${getFullLogisticsUrl(LogisticsUrls.carrierDetail).split(':')[0]}${carrierId}`,
  coOp: (coOpRequestId: number): string => `${NavUrls.coOpDetail.split(':')[0]}${coOpRequestId}`,
  dealer: (accountNum: string): string => `${NavUrls.salesDealer.split(':')[0]}${accountNum}`,
  load: (loadNumber: string): string => `${getFullLogisticsUrl(LogisticsUrls.loadDetail).split(':')[0]}${loadNumber}`,
  logisticsAdmin: (tab: number) => `${getFullLogisticsUrl(LogisticsUrls.admin)}?tab=${tab}`,
  logisticsCalendar: (realativeDate: number | Date) =>
    `${getFullLogisticsUrl(LogisticsUrls.calendar)}?relativeDateNum=${new Date(realativeDate).getTime()}`,
  logisticsOrderLookup: (orderId: string = '') =>
    orderId
      ? `${getFullLogisticsUrl(LogisticsUrls.orderLookupDetail).split(':')[0]}${orderId}`
      : getFullLogisticsUrl(LogisticsUrls.orderLookup),
  logisticsUnallocated: (id: string): string =>
    `${getFullLogisticsUrl(LogisticsUrls.unallocatedDetails).split(':')[0]}${id}`,
  newDealer: (prospectId: number): string => `${NavUrls.newDealer.split(':')[0]}${prospectId}/newDealer`,
  newDealerOrder: (newDealerRequestId: number, brand: string): string =>
    `${NavUrls.newDealerOrder.split(':')[0]}${newDealerRequestId}/${brand}`,
  newsFeedPost: (postId: number): string => `${NavUrls.dealerPortalNewsPost.split(':')[0]}${postId}`,
  prospect: (prospectId: number): string => `${NavUrls.salesProspect.split(':')[0]}${prospectId}`,
  salesCallList: (): string => `${NavUrls.salesCalls /*.split(':')[0].slice(0, -1)*/}`,
  salesCallListWithId: (salesCallId: number): string => `${NavUrls.salesCalls.split(':')[0]}${salesCallId}`,
  salesOrder: (salesId: string): string => `${NavUrls.salesOrder.split(':')[0]}${salesId}`,
  shipment: (shipmentId: number): string =>
    `${getFullLogisticsUrl(LogisticsUrls.shipmentDetail).split(':')[0]}${shipmentId}`
};

export const routes: INav[] = [
  {
    caseSensitive: false,
    element: <lazyComponents.NewsFeedPage />,
    name: 'Dealer Portal - News Feed',
    path: NavUrls.dealerPortalNewsFeed,
    permissionTypes: ['dealerPortal']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.PostPageRouterWrapper />,
    name: 'Dealer Portal - News Feed',
    path: NavUrls.dealerPortalNewsPost,
    permissionTypes: ['dealerPortal']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.DealerPortalFreeShipping />,
    name: 'Dealer Portal - Free Shipping',
    path: NavUrls.dealerPortalFreeShipping,
    permissionTypes: ['dealerPortal']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.CoOpDetailRouterWrapper />,
    name: 'Co-Op Request',
    path: NavUrls.coOpDetail,
    permissionTypes: ['sales', 'finance', 'marketing']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.MarketingAdminPage />,
    name: 'Marketing Admin',
    path: NavUrls.marketingAdmin,
    permissionTypes: ['marketing']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.MarketingNewDealerList />,
    name: 'New Dealer Requests',
    path: NavUrls.marketingNewDealer,
    permissionTypes: ['marketing']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.NewDealerWrapper />,
    name: 'New Dealer',
    path: NavUrls.newDealer,
    permissionTypes: ['finance', 'sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.NewDealerOrderPageWrapper />,
    name: 'New Dealer Order',
    path: NavUrls.newDealerOrder,
    permissionTypes: ['finance', 'sales']
  },
  { caseSensitive: false, name: 'Home', path: NavUrls.root, element: <lazyComponents.Home /> },
  {
    caseSensitive: false,
    element: <lazyComponents.SalesAdminPage />,
    name: 'Sales Admin',
    path: NavUrls.salesAdmin,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.SalesCallRouterWrapper />,
    filterComponent: <lazyComponents.SalesCallFilters />,
    name: 'Sales Calls',
    path: NavUrls.salesCalls,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.CoOpPage />,
    filterComponent: <lazyComponents.SalesCoOpFiscalButton />,
    name: 'Co-Op',
    path: NavUrls.salesCoOp,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.SalesCompliancePage />,
    filterComponent: <lazyComponents.SalesComplianceFilters />,
    name: 'Compliance',
    path: NavUrls.salesCompliance,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.SalesDashboardPage />,
    filterComponent: <lazyComponents.DashboardFilters />,
    name: 'Dashboard',
    path: NavUrls.salesDashboard,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.DealerDetailRouterWrapper />,
    name: 'Dealer',
    path: NavUrls.salesDealer,
    permissionTypes: ['sales', 'logistics']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.DealersListPage />,
    filterComponent: <lazyComponents.DealersFilterButton />,
    name: 'Dealers',
    path: NavUrls.salesDealers,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.LogisticsListPage />,
    filterComponent: <lazyComponents.ShipmentListFilters />,
    name: 'Logistics',
    path: NavUrls.salesLogistics,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.SalesShipmentPage />,
    name: 'Shipment',
    path: NavUrls.salesLogisticsShipment,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.SalesNewDealerList />,
    name: 'New Dealer Requests',
    path: NavUrls.salesNewDealer,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.ProspectDetailPageWrapper />,
    name: 'Prospects',
    path: NavUrls.salesProspect,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.ProspectsListPage />,
    filterComponent: <lazyComponents.ProspectFilters />,
    name: 'Prospects',
    path: NavUrls.salesProspects,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.SalesReportsListPage />,
    name: 'Reports',
    path: NavUrls.salesReports,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.OrderRouterWrapper />,
    name: 'Sales Order',
    path: NavUrls.salesOrder,
    permissionTypes: ['sales', 'finance', 'marketing']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.SalesDocumentsPage />,
    name: 'Sales Documents',
    path: NavUrls.salesDocuments,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.RegionSettingsPage />,
    filterComponent: <lazyComponents.RegionSettingFiscalYearButton />,
    name: 'Region Settings',
    path: NavUrls.salesRegionSettings,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.CoOpManagementPage />,
    filterComponent: <lazyComponents.MarketingCoOpFiscalButton />,
    name: 'Co-Op',
    path: NavUrls.marketingCoOp,
    permissionTypes: ['marketing']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.FinanceCoOpPage />,
    filterComponent: <lazyComponents.FinanceCoOpFiscalButton />,
    name: 'Co-Op Requests',
    path: NavUrls.financeCoOpRequests,
    permissionTypes: ['finance']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.FinancePaymentTerms />,
    name: 'Payment Terms',
    path: NavUrls.financePaymentTerms,
    permissionTypes: ['finance']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.ProgramsPage />,
    filterComponent: <lazyComponents.ProgramsFiscalButton />,
    name: 'Program Setup',
    path: NavUrls.financePrograms,
    permissionTypes: ['finance']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.PreseasonPage />,
    filterComponent: <lazyComponents.PreaseasonFiscalButton />,
    name: 'Pre-Season Setup',
    path: NavUrls.financePreseason,
    permissionTypes: ['finance']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.CashDiscountsPage />,
    filterComponent: <lazyComponents.CashDiscountsFiscalYearButton />,
    name: 'Cash Discounts Setup',
    path: NavUrls.financeCashDiscounts,
    permissionTypes: ['finance']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.FinanceNewDealerList />,
    name: 'New Dealer Requests',
    path: NavUrls.financeNewDealerList,
    permissionTypes: ['finance']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.SalesLinks />,
    name: 'Sales Links',
    path: NavUrls.salesLinks,
    permissionTypes: ['sales']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.FinanceLinks />,
    name: 'Finance Links',
    path: NavUrls.financeLinks,
    permissionTypes: ['finance']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.MarketingLinks />,
    name: 'Marketing Links',
    path: NavUrls.marketingLinks,
    permissionTypes: ['marketing']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.BigCommercePage />,
    name: 'Big Commerce',
    path: NavUrls.marketingBigCommerce,
    permissionTypes: ['marketing']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.InfoTechLinks />,
    name: 'IT Links',
    path: NavUrls.itLinks,
    permissionTypes: ['it']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.ITPage />,
    name: 'IT Displays',
    path: NavUrls.itDisplays,
    permissionTypes: ['it']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.ItPermissions />,
    name: 'IT Permissions',
    path: NavUrls.itPermissions,
    permissionTypes: ['it']
  },
  {
    caseSensitive: false,
    children: logisticsRoutes,
    element: <lazyComponents.LogisticsMainPage />,
    path: `${NavUrls.logistics}/*`,
    permissionTypes: ['logistics', 'sales'],
    searchComponent: <lazyComponents.LogisticsSearchButton />,
    sidebar: {
      component: <lazyComponents.LogisticsSidebar />,
      icon: <LocalOfferIcon />
    }
  },
  {
    caseSensitive: false,
    element: <lazyComponents.OperationsAdmin />,
    name: 'Admin',
    path: NavUrls.operationsAdmin,
    permissionTypes: ['operations']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.OperationsDepartingSoonPage />,
    name: 'Departing Soon',
    path: NavUrls.operationsDepartingSoon,
    permissionTypes: ['operations']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.OperationsUsersPage />,
    name: 'Operations Users',
    path: NavUrls.operationsUsers,
    permissionTypes: ['operations']
  },
  {
    caseSensitive: false,
    element: <lazyComponents.TargetedCatalog />,
    name: 'Targeted Catalog',
    path: NavUrls.targetedCatalog,
    permissionTypes: ['marketing', 'dealerPortal']
  }
];
